/**
 * RevenueCat WebViewGold Integration
 * 
 * This module handles RevenueCat purchases through WebViewGold's revenuecat:// URL scheme.
 * WebViewGold wraps web content in a native app and handles the RevenueCat SDK integration.
 */

import { detectWebViewEnvironment } from '../webviewDetection';
import { supabase } from '../supabase';

// RevenueCat Project Configuration
export const REVENUECAT_CONFIG = {
  projectId: import.meta.env.VITE_REVENUECAT_PROJECT_ID || '',
  publicApiKey: import.meta.env.VITE_REVENUECAT_PUBLIC_API_KEY || '',
};

/**
 * Check if we're running in WebViewGold environment (iPhone only)
 */
export function isWebViewGold(): boolean {
  const webViewInfo = detectWebViewEnvironment();
  // WebViewGold iPhone app - only iOS WebView
  return webViewInfo.isWebView && webViewInfo.platform === 'ios';
}

/**
 * Get current user ID for RevenueCat external_id
 */
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Generate RevenueCat purchase URL for WebViewGold
 * 
 * @param productId - The Product or Entitlement ID from RevenueCat dashboard
 * @param userId - Optional user ID. If not provided, will fetch current user
 * @returns The revenuecat:// URL string
 */
export async function generateRevenueCatPurchaseUrl(
  productId: string,
  userId?: string
): Promise<string> {
  const externalId = userId || await getCurrentUserId() || 'anonymous';
  
  // Build the revenuecat:// URL scheme
  const url = `revenuecat://purchase?external_id=${encodeURIComponent(externalId)}&product=${encodeURIComponent(productId)}`;
  
  return url;
}

/**
 * Trigger a RevenueCat purchase through WebViewGold
 * 
 * @param productId - The Product or Entitlement ID from RevenueCat dashboard
 * @param userId - Optional user ID. If not provided, will fetch current user
 * @returns Promise that resolves when the URL is opened
 */
export async function triggerRevenueCatPurchase(
  productId: string,
  userId?: string
): Promise<void> {
  try {
    const url = await generateRevenueCatPurchaseUrl(productId, userId);
    
    // Create a temporary anchor element to trigger the URL scheme
    const link = document.createElement('a');
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Trigger the click
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    console.log('RevenueCat purchase URL triggered:', url);
  } catch (error) {
    console.error('Error triggering RevenueCat purchase:', error);
    throw error;
  }
}

/**
 * Check if RevenueCat URL scheme is supported
 * This can be used to detect if WebViewGold is properly configured
 */
export function isRevenueCatUrlSchemeSupported(): boolean {
  try {
    // Try to create a link with the revenuecat:// scheme
    const link = document.createElement('a');
    link.href = 'revenuecat://test';
    return link.protocol === 'revenuecat:';
  } catch (error) {
    return false;
  }
}

/**
 * Get recommended product ID based on plan
 * 
 * @param planId - 'monthly' or 'yearly'
 * @returns Product ID string
 * 
 * IMPORTANT: Update these product IDs to match your RevenueCat dashboard!
 * 
 * To find your product IDs:
 * 1. Go to RevenueCat Dashboard → Products
 * 2. Find your products (e.g., "Premium Monthly", "Premium Yearly")
 * 3. Use the Product ID or Entitlement ID from the dashboard
 * 
 * Common examples:
 * - Product IDs: 'premium_monthly', 'premium_yearly'
 * - Entitlement IDs: 'premium' (if using entitlements)
 * 
 * For WebViewGold, you can use either Product ID or Entitlement ID
 */
export function getProductIdForPlan(planId: 'monthly' | 'yearly'): string {
  // These should match your RevenueCat dashboard product IDs
  // TODO: Update these to match your actual RevenueCat product IDs
  const productIds: Record<'monthly' | 'yearly', string> = {
    monthly: 'mindful.family.ltd.Monthly', // Update this to your actual monthly product ID
    yearly: 'mindful.family.ltd.Annual'    // Update this to your actual yearly product ID
  };
  
  return productIds[planId] || productIds.monthly;
}

/**
 * Handle RevenueCat purchase completion
 * This should be called after a successful purchase
 * The webhook will handle the actual subscription update, but this can be used
 * for immediate UI feedback
 */
export async function handlePurchaseCompletion(): Promise<void> {
  try {
    // Refresh subscription status
    // The webhook will update the database, but we can check immediately
    const userId = await getCurrentUserId();
    if (userId) {
      // Optionally trigger a subscription check
      // This would typically be handled by the webhook, but we can add a manual check here
      console.log('Purchase completed, webhook will update subscription status');
    }
  } catch (error) {
    console.error('Error handling purchase completion:', error);
  }
}
